// ---------------------------------------------------------------------------
// SelectionOverlay — renders a floating context toolbar above the selected
// element. Tracks the DOM position of the selected element via
// data-element-id attributes and repositions on scroll/resize.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useStudioStore } from '@/store/studio-store'
import { resolveVisibleElement } from '@/features/page-editor/lib/dom-utils'
import { ContextToolbar } from './context-toolbar'

interface ToolbarPosition {
  top: number
  left: number
  width: number
  height: number
}

export function SelectionOverlay() {
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const isPreview = useEditorStore((s) => s.isPreview)
  const selectedBlockName = useEditorStore((s) => s.selectedBlockName)
  const activeBreakpoint = useEditorStore((s) => s.activeBreakpoint)
  const zoom = useEditorStore((s) => s.zoom)
  const syncedElementIds = useStudioStore((s) => s.syncedElementIds)
  const overlayRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<ToolbarPosition | null>(null)

  // Recompute toolbar position whenever selection, breakpoint, or zoom changes
  const updatePosition = useCallback(() => {
    if (selectedIds.length === 0 || !selectedBlockName) {
      setPos(null)
      return
    }

    const canvasEl = overlayRef.current?.closest('[data-testid="canvas-viewport"]')
    if (!canvasEl) return

    // Find the DOM element for the first selected element
    const selectedId = selectedIds[0]
    const domEl = canvasEl.querySelector(
      `[data-element-id="${selectedId}"]`,
    ) as HTMLElement | null
    if (!domEl) {
      setPos(null)
      return
    }

    // Resolve to visible element for display:contents wrappers
    const visibleEl = resolveVisibleElement(domEl)

    // Use overlay container rect (not canvasEl) since overlays are positioned
    // inside <div className="relative"> within DeviceFrame, not the canvas viewport.
    // getBoundingClientRect() returns screen-space (post-zoom-transform) values,
    // but CSS top/left operate in local (pre-transform) space, so divide by zoom.
    const containerRect = overlayRef.current!.getBoundingClientRect()
    const elRect = visibleEl.getBoundingClientRect()
    const z = zoom || 1

    setPos({
      top: (elRect.top - containerRect.top) / z,
      left: (elRect.left - containerRect.left) / z,
      width: elRect.width / z,
      height: elRect.height / z,
    })
  }, [selectedIds, selectedBlockName, activeBreakpoint, zoom])

  // Update position on selection change, scroll, and resize
  useEffect(() => {
    updatePosition()

    const canvasEl = overlayRef.current?.closest('[data-testid="canvas-viewport"]')
    if (!canvasEl) return

    // Debounce MutationObserver with RAF to avoid excessive re-renders
    let rafId = 0
    const debouncedUpdate = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(updatePosition)
    }

    canvasEl.addEventListener('scroll', updatePosition, { passive: true })
    window.addEventListener('resize', updatePosition)

    const observer = new MutationObserver(debouncedUpdate)
    observer.observe(canvasEl, { childList: true, subtree: true })

    return () => {
      cancelAnimationFrame(rafId)
      canvasEl.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
      observer.disconnect()
    }
  }, [updatePosition])

  if (isPreview || selectedIds.length === 0) return null

  return (
    <div
      ref={overlayRef}
      className="pointer-events-none absolute inset-0"
      style={{ zIndex: 50 }}
      data-testid="selection-overlay"
    >
      {/* Multi-select count badge */}
      {selectedIds.length > 1 && selectedBlockName && (
        <div
          className="pointer-events-auto absolute top-2 right-2 text-white px-2 py-0.5 rounded text-xs font-medium"
          style={{ backgroundColor: 'hsl(var(--editor-selection))' }}
        >
          {selectedIds.length} selected
        </div>
      )}

      {/* Floating context toolbar — above element, or below if near top */}
      {pos && selectedBlockName && (() => {
        const toolbarH = 36
        const gap = 8
        const showBelow = pos.top < toolbarH + gap
        const top = showBelow
          ? pos.top + pos.height + gap
          : pos.top - toolbarH - gap
        return (
          <div
            className="pointer-events-auto absolute"
            style={{
              top: Math.max(4, top),
              left: pos.left + pos.width / 2,
              transform: 'translateX(-50%)',
            }}
            data-testid="context-toolbar-anchor"
          >
            <ContextToolbar
              selectedIds={selectedIds}
              blockName={selectedBlockName}
            />
          </div>
        )
      })()}

      {/* Cross-tab synced element amber overlays (SYNC-03) */}
      {Array.from(syncedElementIds).map((elementId) => {
        if (selectedIds.includes(elementId)) return null
        const canvasEl = overlayRef.current?.closest('[data-testid="canvas-viewport"]')
        if (!canvasEl) return null
        const el = canvasEl.querySelector(`[data-element-id="${elementId}"]`) as HTMLElement | null
        if (!el) return null
        const visibleEl = resolveVisibleElement(el)
        const containerRect = overlayRef.current!.getBoundingClientRect()
        const elRect = visibleEl.getBoundingClientRect()
        const z = zoom || 1
        return (
          <div
            key={`sync-${elementId}`}
            className="absolute pointer-events-none ring-1 ring-amber-400/50 rounded sync-highlight-pulse"
            style={{
              left: (elRect.left - containerRect.left) / z,
              top: (elRect.top - containerRect.top) / z,
              width: elRect.width / z,
              height: elRect.height / z,
            }}
          />
        )
      })}
    </div>
  )
}
